import React, { useState, useRef, useEffect } from 'react';
import { Switch, Button, Col, Drawer, Form, Input, Row, Select, Space, message, Typography, Card, Divider } from 'antd';
import { CloseOutlined, CheckOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getTemplate, createTemplate, updateTemplate } from '../../../../apis/TemplateEmailApi';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const tiposTemplate = [
  { value: 'nomina_recibo', label: 'Recibo de Nómina' },
  { value: 'bienvenida', label: 'Bienvenida' },
  { value: 'despedida', label: 'Despedida' },
  { value: 'notificacion', label: 'Notificación' },
  { value: 'otro', label: 'Otro' },
];

const idiomasDisponibles = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'Inglés' },
];

const App = ({ title, id, open, finish }) => {
    const [form] = Form.useForm();
    const [ID, setID] = useState(id);
    const [modified, setModified] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            if (!values) return;
        } catch (errorInfo) {
            console.log('Failed:', errorInfo);
            return;
        }

        setLoading(true);
        const fields = form.getFieldsValue();
        const payload = {
            codigo: fields.codigo,
            nombre: fields.nombre,
            tipo: fields.tipo,
            idioma: fields.idioma,
            descripcion: fields.descripcion,
            subject_template: fields.subject_template,
            body_html_template: fields.body_html_template,
            body_text_template: fields.body_text_template,
            is_default: fields.is_default,
            is_active: fields.is_active
        };

        let success = false;
        
        if (ID > 0) {
            // Update
            const response = await updateTemplate(ID, payload);
            success = response?.ok;
            if (response?.ok) {
                message.success('¡Plantilla actualizada correctamente!');
            } else {
                message.error(response?.message || 'Error al actualizar plantilla');
            }
        } else {
            // Create
            const response = await createTemplate(payload);
            success = response?.ok;
            if (response?.ok) {
                setID(response.payload);
                message.success('¡Plantilla creada correctamente!');
            } else {
                message.error(response?.message || 'Error al crear plantilla');
            }
        }

        setLoading(false);
        if (success) {
            setModified(true);
            onClose(true);
        }
    };

    const onClose = (reload) => {
        finish(reload ?? modified);
        setInitialValues(null);
    };

    const effectRan = useRef(false);
    useEffect(() => {
        if (!effectRan.current) {
            (async () => {
                if (ID > 0) {
                    await refreshData();
                } else {
                    setInitialValues({
                        idioma: 'es',
                        is_default: false,
                        is_active: true,
                    });
                }
                setModified(false);
            })();
        }
        return () => effectRan.current = true;
    }, []);

    const refreshData = async () => {
        const response = await getTemplate(ID);
        if (response?.ok) {
            setInitialValues(response.payload);
        }
    };

    return (
        <Drawer
            title={title}
            width={900}
            placement="right"
            onClose={onClose}
            open={open}
            closable={true}
            extra={
                <Space>
                    <Button onClick={onClose}>Cancelar</Button>
                    <Button onClick={handleOk} type="primary" loading={loading}>
                        Guardar
                    </Button>
                </Space>
            }
        >
            {initialValues && (
                <Form
                    layout="vertical"
                    form={form}
                    autoComplete="off"
                    initialValues={initialValues}
                    onFinish={handleOk}
                >
                    <Card 
                        title="Información General" 
                        size="small" 
                        style={{ marginBottom: 16 }}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="codigo"
                                    label="Código"
                                    rules={[{ required: true, message: 'Por favor ingresa el código' }]}
                                    tooltip="Identificador único de la plantilla"
                                >
                                    <Input 
                                        placeholder="ej: recibo_nomina_v1" 
                                        maxLength={100}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="nombre"
                                    label="Nombre"
                                    rules={[{ required: true, message: 'Por favor ingresa el nombre' }]}
                                >
                                    <Input 
                                        placeholder="ej: Plantilla Recibo de Nómina" 
                                        maxLength={255}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="tipo"
                                    label="Tipo"
                                    rules={[{ required: true, message: 'Por favor selecciona el tipo' }]}
                                >
                                    <Select placeholder="Selecciona el tipo">
                                        {tiposTemplate.map((t) => (
                                            <Option key={t.value} value={t.value}>
                                                {t.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item 
                                    name="idioma" 
                                    label="Idioma"
                                    rules={[{ required: true, message: 'Por favor selecciona el idioma' }]}
                                >
                                    <Select placeholder="Selecciona el idioma">
                                        {idiomasDisponibles.map((i) => (
                                            <Option key={i.value} value={i.value}>
                                                {i.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item name="descripcion" label="Descripción">
                                    <TextArea 
                                        rows={2} 
                                        placeholder="Descripción breve de la plantilla" 
                                        maxLength={500}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <Card 
                        title="Contenido del Email" 
                        size="small" 
                        style={{ marginBottom: 16 }}
                    >
                        <Form.Item
                            name="subject_template"
                            label="Asunto del Correo"
                            rules={[{ required: true, message: 'Por favor ingresa el asunto' }]}
                            tooltip="Puede usar variables Jinja2"
                        >
                            <Input 
                                placeholder="ej: Recibo de Nómina - {{ empleado.nombre }}" 
                                maxLength={255}
                            />
                        </Form.Item>

                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
                            <InfoCircleOutlined /> Variables disponibles: <Text code>empleado</Text>, <Text code>company</Text>, <Text code>periodo</Text>
                        </Paragraph>

                        <Form.Item
                            name="body_html_template"
                            label="Cuerpo HTML"
                            rules={[{ required: true, message: 'Por favor ingresa el cuerpo HTML' }]}
                            tooltip="HTML con soporte de variables Jinja2"
                        >
                            <TextArea
                                rows={12}
                                placeholder={`
                                    <html>
                                        <body>
                                            <h1>Hola {{ empleado.nombre }},</h1>
                                            <p>Tu recibo de nómina del período {{ periodo.num_periodo }}/{{ periodo.anio }} está listo.</p>
                                            <p>Saludos,<br/>{{ company.name }}</p>
                                        </body>
                                    </html>`}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="body_text_template"
                            label="Cuerpo Texto Plano (opcional)"
                            tooltip="Versión en texto plano para clientes que no soportan HTML"
                        >
                            <TextArea
                                rows={6}
                                placeholder={`
                                    Hola {{ empleado.nombre }},
                                    Tu recibo de nómina del período {{ periodo.num_periodo }}/{{ periodo.anio }} está listo.
                                    Saludos,
                                    {{ company.name }}`}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                            />
                        </Form.Item>
                    </Card>

                    <Card title="Configuración" size="small">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item 
                                    name="is_default" 
                                    label="Plantilla Predeterminada" 
                                    valuePropName="checked"
                                    tooltip="Marcar como plantilla por defecto para este tipo"
                                >
                                    <Switch
                                        checkedChildren={<CheckOutlined />}
                                        unCheckedChildren={<CloseOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item 
                                    name="is_active" 
                                    label="Activa" 
                                    valuePropName="checked"
                                >
                                    <Switch
                                        checkedChildren={<CheckOutlined />}
                                        unCheckedChildren={<CloseOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
                </Form>
            )}
        </Drawer>
    );
};

export default App;
